import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Stack,
    SxProps,
    Typography,
} from '@mui/material';
import { headingFontBold, bodyFont, vars, labelFontSemiBold } from '../../../../../Theme';
import CheckboxTree from '../../_parts/CheckboxTree';
import { ICreateReportFromTemplateMainState } from '../CreateReportDialog';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import i18n from '../../../../../localization/i18n';
import Message from '../../_parts/Message';
import { mdiAlertCircle } from '@mdi/js';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

export default ({
    label,
    docTypeOptions,
    mainState,
    field,
    handleChange,
    showErrorState,
}: IContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'createReportDialog' });

    const docTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);

    const docTypeItems = docTypes
        .filter(x => docTypeOptions.includes(x.name))
        .map(x => ({
            id: x.name,
            path: x.name,
            label: x.translations[i18n.language],
            parent: '',
            icon: {
                childIcon: {
                    path: x.icon,
                    color: x.color,
                },
            },
        }));

    const handleChangeCheckbox = (value: string, checked: boolean) => {
        handleChange({
            field: field,
            value: mainState[field].filter(opt => opt !== value).concat(checked ? value : []),
        });
    };

    const handleDocTypeSelection = (_, checked: boolean) => {
        handleChange({
            field: field,
            value: checked ? docTypeItems.map(docTypeItem => docTypeItem.id) : [],
        });
    };

    return (
        <Stack direction='column' spacing={'12px'} sx={{ width: '512px' }}>
            <FormControl size='small'>
                <Stack
                    sx={{
                        pb: '4px',
                    }}
                >
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: 'space-between',
                            height: '28px',
                        }}
                    >
                        <FormLabel sx={{ ...formLabelSx, marginTop: 0 }}>{label}</FormLabel>
                    </Stack>
                    <Stack
                        direction='row'
                        sx={{
                            justifyContent: 'space-between',
                            height: '28px',
                            alignItems: 'center',
                        }}
                    >
                        <Typography
                            sx={{
                                ...bodyFont,
                                fontSize: '14px',
                                color: vars.font,
                                fontWeight: 400,
                            }}
                        >
                            {`${t('selected')}: ${mainState.selectedDocumentTypes.length}`}
                        </Typography>

                        <FormControlLabel
                            label={
                                mainState.selectedDocumentTypes.length !== docTypeItems.length
                                    ? t('selectAll')
                                    : t('clear')
                            }
                            labelPlacement='start'
                            sx={{
                                '& .MuiFormControlLabel-label': {
                                    fontFamily: labelFontSemiBold,
                                    fontSize: '14px',
                                },
                            }}
                            control={
                                <Checkbox
                                    onChange={handleDocTypeSelection}
                                    checked={
                                        mainState.selectedDocumentTypes.length ===
                                        docTypeItems.length
                                    }
                                />
                            }
                        />
                    </Stack>
                </Stack>
                {showErrorState && (
                    <Message
                        text={t('errorSelectDocumentTypes')}
                        icon={mdiAlertCircle}
                        color={vars.errorDialogRed}
                        typographySx={{ fontWeight: 700 }}
                        wrapperSx={{
                            border: 'none',
                            padding: '2px',
                            mb: 1,
                        }}
                    />
                )}
                <FormGroup>
                    <CheckboxTree
                        withSearch
                        items={docTypeItems}
                        checkedNodes={mainState.selectedDocumentTypes}
                        halfCheckedNodes={[]}
                        onCheckNode={handleChangeCheckbox}
                    />
                </FormGroup>
            </FormControl>
        </Stack>
    );
};

interface IContentProps {
    label: string;
    docTypeOptions: string[];
    mainState: ICreateReportFromTemplateMainState;
    field: string;
    handleChange: ({ field, value }: { field: string; value: string | string[] }) => void;
    showErrorState: boolean;
}
